"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.URL_PATTERN = exports.safeEncodeUriComponent = exports.getAssetPrefix = exports.prefixStaticAsset = void 0;
const exenv_1 = require("exenv");
const config_1 = __importDefault(require("next/config"));
const log_1 = require("../../../log");
const StringHelper_1 = require("../../objects/StringHelper");
const ThemeAssetHelper_1 = require("../../theme/ThemeAssetHelper");
const UrlBuilder_1 = __importDefault(require("./UrlBuilder"));
const log = (0, log_1.getLog)(module);
/**
 * Utility for building URLs for Aurora
 *
 * @author Adam Ayres
 */
const IMAGE_SIZE_PARAM = 'image-size';
const IMAGE_DIMENSIONS_PARAM = 'image-dimensions';
const IMAGE_CONSTRAIN_PARAM = 'constrain-image';
const IMAGE_COORDINATES_PARAM = 'image-coordinates';
const LIA_API_VERSION = '2.1';
const LIA_GRAPHQL_PATH = `/api/${LIA_API_VERSION}/graphql`;
const LIA_AUTH_PATH = `/api/${LIA_API_VERSION}/auth`;
// eslint-disable-next-line unicorn/no-static-only-class
class UrlHelper {
    /**
     * Modify an existing LIA image asset URL
     *
     * @param url - the image server URL to adjust
     * @param dimensions - the dimensions of the image; cannot be used in conjunction with <code>size</code> param.
     * Dimensions wins.
     * @param constrain - whether the image should be constrained to the original aspect ratio
     * @param coordinates - the coordinates of where to crop the image from
     * @param appendExisting - whether to replace the params in the url argument
     */
    static adjustImageServerPageUrl(url, dimensions, constrain = false, coordinates, appendExisting = true) {
        if (url.startsWith('https://loremflickr.com/')) {
            const urlBuilder = UrlBuilder_1.default.fromUrl(url);
            if (dimensions) {
                return urlBuilder
                    .removePathItemByIndex(1)
                    .removePathItemByIndex(0)
                    .addPath(`${dimensions.height}`)
                    .addPath(`${dimensions.width}`)
                    .build();
            }
            return urlBuilder.build();
        }
        const urlBuilder = UrlBuilder_1.default.fromUrl(url);
        if (url.includes('/images/') ||
            url.includes('/uploaded_images/') ||
            url.includes('/m_assets/')) {
            const queryParameters = {};
            if (dimensions) {
                queryParameters[IMAGE_DIMENSIONS_PARAM] = (0, ThemeAssetHelper_1.convertDimensions)(dimensions);
            }
            if (constrain) {
                queryParameters[IMAGE_CONSTRAIN_PARAM] = 'true';
            }
            if (url.includes(IMAGE_COORDINATES_PARAM)) {
                return urlBuilder.addQuery(queryParameters).build();
            }
            if (coordinates) {
                queryParameters[IMAGE_COORDINATES_PARAM] = this.convertCoordinates(coordinates);
            }
            urlBuilder.replaceQuery(queryParameters, appendExisting);
            return urlBuilder.build();
        }
        else if (!url.includes('image/serverpage/image-id')) {
            return url;
        }
        else {
            if (dimensions) {
                urlBuilder.removePathItem(IMAGE_SIZE_PARAM);
                urlBuilder.addPathItems(IMAGE_DIMENSIONS_PARAM, (0, ThemeAssetHelper_1.convertDimensions)(dimensions));
            }
            if (constrain) {
                urlBuilder.addPathItems(IMAGE_CONSTRAIN_PARAM, 'true');
            }
            if (coordinates) {
                urlBuilder.addPathItems(IMAGE_COORDINATES_PARAM, this.convertCoordinates(coordinates));
            }
        }
        return urlBuilder.build();
    }
    /**
     * Convert the specified coordinates to a param string
     * @param coordinates
     */
    static convertCoordinates(coordinates) {
        return `${coordinates.left},${coordinates.top},${coordinates.right},${coordinates.bottom}`;
    }
    /**
     * Converts spaces,dot(.) and underscore(_) to `-` and makes all characters lowercase.
     * Also omits any special characters. Non ASCII characters are percent encoded.
     * We do not do any other encoding since NextJs's `router/link` already
     * has some built-in encoding for path param values.
     *
     * @param value - the value to convert.
     */
    static convertValueForPath(value) {
        var _a;
        return ((_a = value === null || value === void 0 ? void 0 : value.trim().replaceAll(/[\s._]+/g, '-').replaceAll(/&amp;|&quot;|&lt;|&gt;|&nbsp;/g, '').replaceAll(/(<([^>]+)>)/gi, '').replaceAll(/[^\dA-Za-z\u0080-\uFFFD-]/g, '').toLowerCase()) !== null && _a !== void 0 ? _a : '');
    }
    /**
     * For a given LIA tenant, return the path to the graphQL API.
     *
     * @param tenant - the tenant hostname
     */
    static getGraphQlApiUrl(tenant) {
        return UrlBuilder_1.default.fromUrl(UrlHelper.getTenantHost(tenant)).addPath(LIA_GRAPHQL_PATH);
    }
    /**
     * For a given LIA tenant, return the path to the auth API.
     *
     * @param tenant - the tenant hostname.
     * @param resourcePath - the resource path to append to the auth API.
     */
    static getAuthApiUrl(tenant, resourcePath) {
        return UrlBuilder_1.default.fromUrl(UrlHelper.getTenantHost(tenant))
            .addPath(LIA_AUTH_PATH)
            .addPath(resourcePath);
    }
    /**
     * During SSR, use the correct host for appropriate LIA instances, otherwise use external facing host URL.
     *
     * Also, if a tenant is so configured, prefix a path to route requests to it.
     * @param tenant the tenant
     * @param endingSlash add a trailing slash to context if it doesn't have one
     * @param isomorphic if true, bypasses hostname for server calls.  If false, the hostname will be the same for both
     *  client and server calls.
     */
    static getTenantHost(tenant, endingSlash = false, isomorphic = true) {
        const tenantHost = !exenv_1.canUseDOM && process.env.LIA_TENANT_HOST;
        const useWaf = UrlHelper.isWafEnabled();
        const useLocal = !exenv_1.canUseDOM && process.env.USE_LOCAL_LIA === 'true';
        const host = isomorphic && tenantHost
            ? tenantHost
            : useWaf && isomorphic
                ? this.getWafHost(tenant)
                : useLocal && isomorphic
                    ? this.getLocalHost(tenant)
                    : tenant === null || tenant === void 0 ? void 0 : tenant.host;
        if (!host) {
            throw new Error('No valid host specified on tenant!');
        }
        let path = tenant === null || tenant === void 0 ? void 0 : tenant.liaContextPath;
        if (path) {
            if (!path.startsWith('/')) {
                path = `/${path}`;
            }
            if (endingSlash && !path.endsWith('/')) {
                path += '/';
            }
            return `${host}${path}`;
        }
        return host;
    }
    /**
     * Is Aurora configured to call LIA via the WAF?
     */
    static isWafEnabled() {
        return !exenv_1.canUseDOM && process.env.WAF_HEADER != null && process.env.WAF_KEY != null;
    }
    /**
     * For a given tenant, if we're running on the server, creates a "local" URL to call the LIA instance directly.
     * @param tenant
     * @return a hostname with a scheme prefixed
     */
    static getLocalHost(tenant) {
        var _a, _b, _c;
        const privateConfig = tenant.privateConfig ? tenant.privateConfig() : null;
        const port = (_b = (_a = privateConfig === null || privateConfig === void 0 ? void 0 : privateConfig.port) !== null && _a !== void 0 ? _a : process.env.LIA_PORT) !== null && _b !== void 0 ? _b : 8080;
        const ssl = (_c = privateConfig === null || privateConfig === void 0 ? void 0 : privateConfig.https) !== null && _c !== void 0 ? _c : false;
        return `http${ssl ? 's' : ''}://127.0.0.1:${port}`;
    }
    /**
     * For a given tenant, if we're running on the server, creates a URL to call the LIA instance via the load balancer.
     * @return a hostname with a scheme prefixed
     */
    static getWafHost(tenant) {
        var _a;
        return ((_a = process.env.WAF_HOST) !== null && _a !== void 0 ? _a : `https://origin-${tenant.phase == 'prod' ? tenant.id.replaceAll('.prod', '') : tenant.id}.lithium.com`);
    }
    /**
     * If a prefix exists and is supported, prepend the prefix to the existing path.
     * @param prefix an optional prefix, starting with http protocol or a forward slash.
     * @param path a non-empty path
     */
    static prefix(prefix, path) {
        if (prefix && !path.startsWith('http') && !path.startsWith('./') && !path.startsWith('../')) {
            const url = prefix.startsWith('http') || prefix.startsWith('/') ? prefix : `/${prefix}`;
            return new UrlBuilder_1.default(url).addPath(path).build();
        }
        else {
            return path;
        }
    }
    /**
     * Prefix a path with a tenant's base path. When no tenant is passed default to BASE_PATH property.
     *
     * @param tenant a tenant
     * @param path a non-empty path
     */
    static prefixBasePath(tenant, path) {
        return UrlHelper.prefix(tenant === null || tenant === void 0 ? void 0 : tenant.basePath, path);
    }
    /**
     * If the hostname does not have a scheme attached, add it.
     * @param hostname
     * @param ssl Whether or not to appends https.
     */
    static prefixSchemeIfNecessary(hostname, ssl) {
        return !hostname.startsWith('http') ? `http${ssl ? 's' : ''}://${hostname}` : hostname;
    }
    /**
     * If the hostname has a scheme, remove it.
     * @param hostname
     */
    static removeSchemeIfNecessary(hostname) {
        return hostname === null || hostname === void 0 ? void 0 : hostname.replace(/(^\w+:|^)\/\//, '');
    }
    /**
     * Determines a slug to use for a message's URL path.
     *
     * @param message
     */
    static determineSlugForMessagePath(message) {
        if (message) {
            const { subject, seoTitle, searchSnippet, seoDescription, body } = message;
            const slug = UrlHelper.convertValueForPath(subject) ||
                UrlHelper.convertValueForPath(seoTitle) ||
                UrlHelper.convertValueForPath(searchSnippet) ||
                UrlHelper.convertValueForPath(seoDescription) ||
                UrlHelper.convertValueForPath(body);
            const newSlug = slug || '-';
            return (0, StringHelper_1.replaceLoneSurrogates)(newSlug.slice(0, 80));
        }
        return null;
    }
    /**
     * Determines whether the given string is a URL or not
     *
     * @param value The string to be checked if it is a URL
     * @returns boolean
     */
    static isValidHTTPUrl(value) {
        let url;
        try {
            url = new URL(value);
        }
        catch (_a) {
            return false;
        }
        return url.protocol === 'http:' || url.protocol === 'https:';
    }
    /**
     * Are the two hostnames the same?
     * @param hostname1
     * @param hostname2
     */
    static isSameSite(hostname1, hostname2) {
        return (UrlHelper.removeSchemeIfNecessary(hostname1) === UrlHelper.removeSchemeIfNecessary(hostname2));
    }
    /**
     * Returns the url if is safe otherwise return empty.
     * @param url - the actual url
     */
    static sanitize(url) {
        const invalidProtocolRegex = /^(\W*)(javascript|data)/im;
        const ctrlCharactersRegex = /[\u0000-\u001F\u007F-\u009F\u2000-\u200D\uFEFF]/gim;
        const urlSchemeRegex = /^([^:]+):/gm;
        const sanitizedUrl = UrlHelper.decodeHtmlCharacters(url || '')
            .replaceAll(ctrlCharactersRegex, '')
            .trim();
        if (!sanitizedUrl) {
            return '';
        }
        const urlSchemeParseResults = sanitizedUrl.match(urlSchemeRegex);
        if (!urlSchemeParseResults) {
            return sanitizedUrl;
        }
        if (invalidProtocolRegex.test(urlSchemeParseResults[0])) {
            return '';
        }
        return sanitizedUrl;
    }
    /**
     * Decode html text into html entity and return it
     * @param string
     */
    static decodeHtmlCharacters(string) {
        const htmlEntitiesRegex = /&#(\w+)(^\w|;)?/g;
        return string.replaceAll(htmlEntitiesRegex, (match, dec) => {
            return String.fromCodePoint(dec);
        });
    }
    /**
     * Returns a UrlBuilder with the tenant base url and port if in development environment.
     * @param tenant
     */
    static getBaseUrl(tenant) {
        var _a, _b;
        const isDevelopmentEnv = process.env.NODE_ENV === 'development';
        const baseUrl = (_b = (_a = (exenv_1.canUseDOM ? window.location.origin : tenant === null || tenant === void 0 ? void 0 : tenant.baseUrl)) !== null && _a !== void 0 ? _a : tenant === null || tenant === void 0 ? void 0 : tenant.baseUrl) !== null && _b !== void 0 ? _b : '';
        const port = exenv_1.canUseDOM ? window.location.port : process.env.PORT;
        const appPort = isDevelopmentEnv && !!port ? port : '';
        return UrlBuilder_1.default.fromUrl(baseUrl).port(appPort);
    }
    /**
     * Returns a UrlBuilder with the community root url, composed of the tenant baseUrl suffixed with the basePath if
     * it exists.
     * If this is meant to be used with a url path, then call {@link getFullyQualifiedUrlForPath} instead.
     * @param tenant the tenant
     * @param includeCommunityNameInPath whether to include the community name in the path.
     */
    static getCommunityUrl(tenant, includeCommunityNameInPath = false) {
        const url = this.getBaseUrl(tenant);
        if (tenant === null || tenant === void 0 ? void 0 : tenant.basePath) {
            url.addPath(tenant.basePath);
        }
        if (includeCommunityNameInPath && (tenant === null || tenant === void 0 ? void 0 : tenant.community)) {
            url.addPath(tenant.community);
        }
        return url;
    }
    /**
     * Appends a path to the community baseUrl, including the basePath when needed.
     * @param tenant
     * @param path
     */
    static getFullyQualifiedUrlForPath(tenant, path = '') {
        let baseUrl = this.getBaseUrl(tenant);
        if ((tenant === null || tenant === void 0 ? void 0 : tenant.basePath) && (!path || !path.startsWith(`/${tenant.basePath}`))) {
            baseUrl = baseUrl.addPath(tenant.basePath);
        }
        return baseUrl.addPath(path).build();
    }
}
exports.default = UrlHelper;
/**
 * Prefix any static asset path with an asset prefix, if configured.
 *
 * @param path a non-empty path
 */
function prefixStaticAsset(path) {
    var _a, _b;
    let result = UrlHelper.prefix((_b = (_a = (0, config_1.default)()) === null || _a === void 0 ? void 0 : _a.publicRuntimeConfig) === null || _b === void 0 ? void 0 : _b.assetPrefix, path);
    if (path.endsWith('/') && !result.endsWith('/')) {
        result += '/';
    }
    return result;
}
exports.prefixStaticAsset = prefixStaticAsset;
/**
 * Returns the assets prefix to be used in URLs. Prefix includes the BASE_PATH if configured. Meant to be called
 * during Next.js configuration.
 */
function getAssetPrefix() {
    const basePath = process.env.BASE_PATH;
    const liaUrl = process.env.LIA_URL;
    const assetPrefix = process.env.ASSET_PREFIX;
    if (basePath && assetPrefix && assetPrefix.startsWith('/')) {
        const finalAssetPrefix = assetPrefix.length > 1 ? `${basePath}${assetPrefix}` : basePath;
        log.info('Using asset prefix: %s', finalAssetPrefix);
        return finalAssetPrefix;
    }
    else if (basePath && basePath.startsWith('/') && liaUrl) {
        // if we're using a base path, we need to use a fully qualified asset prefix for routes to work as expected
        const finalAssetPrefix = `${liaUrl}${basePath}`;
        log.info('Using asset prefix: %s', finalAssetPrefix);
        return finalAssetPrefix;
    }
    log.info('Using asset prefix: %s', assetPrefix);
    return assetPrefix;
}
exports.getAssetPrefix = getAssetPrefix;
/**
 * Wrapper around the native `encodeURIComponent` that removes lone surrogates.
 *
 * https://jira.dev.lithium.com/browse/LIA-88879
 *
 * @param uriComponent A component of a URI
 */
function safeEncodeUriComponent(uriComponent) {
    return encodeURIComponent((0, StringHelper_1.replaceLoneSurrogates)(uriComponent.toString()));
}
exports.safeEncodeUriComponent = safeEncodeUriComponent;
/**
 * This regex pattern has three capturing groups to validate the protocol, domain, the domain extensions along with path/port/queryParam/fragment
 * (http|https) is the capturing group that verifies a protocol
 * \/\/ validates //
 * [\dA-Za-z]+ makes sure that domain name begins with a word character
 * (.[\dA-Za-z]+)+ is a capturing group that validates the domain name
 * ([\d#%&+,.\/:=?@A-Z^a-z~-]*[\d#%&+\/;=?@A-Z^a-z~-])? validates the domain extension and the variables in a URL. It
 * has two parts -
 *  1) [\d#%&+,.\/:=?@A-Z^a-z~-]* validates characters that indicate the start of a url path/port/queryString
 *  2) [\d#%&+\/;=?@A-Z^a-z~-] validates the values for port/path/queryStringParameters/fragments
 *
 * @returns the RegExp for url validation
 */
exports.URL_PATTERN = new RegExp(/(http|https):\/\/\w+(.\w+)+([\d#%&+,./:=?@A-Z^a-z~-]*[\d#%&+/;=?@A-Z^a-z~-])?/);
//# sourceMappingURL=UrlHelper.js.map